import { useSendMessageContact } from "@/api/contact";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2Icon, MailIcon } from "lucide-react";
import { HTMLProps, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { CustomSheetFooter } from "../ui/custom-sheet";
import { Form, FormControl, FormField, FormItem, FormLabel } from "../ui/form";
import { FormCol } from "../ui/form-col";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../ui/sheet";
import { Textarea } from "../ui/textarea";

const MessageSentView = ({
  className,
  ...props
}: Omit<HTMLProps<HTMLDivElement>, "children">) => {
  const { t } = useTranslation();

  return (
    <div
      className={cn("flex flex-col gap-2 items-center text-center", className)}
      {...props}
    >
      <CheckCircle2Icon className="stroke-blue-500" />
      <h2 className="font-semibold text-2xl">
        {t("messageEnvoyeTitreFormulaireContactLabel")}
      </h2>
      <h3>{t("messageEnvoyeSousTitreFormulaireContactLabel")}</h3>
    </div>
  );
};

const schema = z.object({
  subject: z.string().min(1),
  message: z.string().min(1),
  sendCopyToUser: z.boolean().default(false),
});

// TODO: traductions
const subjectOptions: string[] = [
  "questionUtilisationAppFormulaireContactLabel",
  "problemeConnexionUtilisateurFormulaireContactLabel",
  "modifierAbonnementFormulaireContactLabel",
  "questionFacturationFormulaireContactLabel",
  "besoinAideSpecifiqueFormulaireContactLabel",
  "autreQuestionFormulaireContactLabel",
];

export const ContactFormModal = () => {
  const { t } = useTranslation();

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      message: "",
      subject: "",
      sendCopyToUser: false,
    },
  });

  const messageSender = useSendMessageContact();

  const [successfullySent, setSuccessfullySent] = useState(false);
  const [animate, setAnimate] = useState(false);

  function onSubmit(values: z.infer<typeof schema>) {
    // TODO: traduire avant d'envoyer
    messageSender.mutate(
      { ...values, subject: t(values.subject) },
      {
        onSuccess: () => {
          setAnimate(true);
          setSuccessfullySent(true);
        },
        onError: () => {
          toast({
            title: t("titreErreurServeurFormulaireContact"),
            description: t("descriptionErreurServeurFormulaireContact"),
            variant: "error",
            duration: 1000,
          });
        },
      },
    );
  }

  function onSendNewMessage() {
    form.reset();
    setAnimate(false);
    setSuccessfullySent(false);
  }

  return (
    <Sheet
      onOpenChange={(open) => {
        if (open === false && successfullySent) onSendNewMessage();
      }}
    >
      <SheetTrigger>
        <Button variant="ghost" className="p-2.5 rounded-lg">
          <MailIcon className="w-5 h-5" />
        </Button>
      </SheetTrigger>
      <SheetContent className="sm:max-w-full w-[500px] px-0">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className={`flex flex-col h-full`}
          >
            <SheetHeader className="p-8 py-4 border-b">
              <SheetTitle>{t("titreFormulaireContactLabel")}</SheetTitle>
            </SheetHeader>
            <div className="h-full p-8 gap-8">
              <FormCol
                className={`${successfullySent === true && "h-0 overflow-y-hidden"}`}
              >
                <FormField
                  name="subject"
                  {...form}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("objetFormulaireContactLabel")} *
                      </FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={(value) =>
                            value && field.onChange(value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={t(
                                "placeholderObjetFormulaireContactLabel",
                              )}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {subjectOptions.map((o) => (
                              <SelectItem key={o} value={o}>
                                {t(o)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("messageFormulaireContactLabel")} *
                      </FormLabel>
                      <FormControl>
                        <Textarea className="min-h-[150px]" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  name="sendCopyToUser"
                  render={({ field }) => (
                    <FormItem className="flex space-y-0 flex-row gap-2 items-center">
                      <FormControl>
                        <Checkbox
                          onCheckedChange={field.onChange}
                          checked={field.value}
                        />
                      </FormControl>
                      <FormLabel>
                        {t("copieUtilisateurFormulaireContactLabel")}
                      </FormLabel>
                    </FormItem>
                  )}
                />
              </FormCol>
              <MessageSentView
                className={`${successfullySent === true ? "opacity-100" : "opacity-0"} ${animate === true ? "transition-opacity duration-700" : ""}`}
              />
            </div>
            <CustomSheetFooter>
              <div
                className={`flex flex-row sm:justify-around ${successfullySent === true && "h-0 overflow-y-hidden"}`}
              >
                <Button
                  type="button"
                  variant={"link"}
                  onClick={onSendNewMessage}
                >
                  {t("reinitialiserFormulaireContactLabel")}
                </Button>
                <Button type="submit" variant={"default"}>
                  {t("envoyerFormulaireContactLabel")}
                </Button>
              </div>
              <div
                className={`flex flex-row justify-center ${successfullySent === false && "h-0 overflow-y-hidden"}`}
              >
                <Button type="button" onClick={onSendNewMessage}>
                  {t("envoyerAutreMessageFormulaireContactLabel")}
                </Button>
              </div>
            </CustomSheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
